export const REMINDER_SCHEMA_VERSION = 1

export type ReminderFrequency = 'daily' | 'weekly' | 'monthly'
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6
export type WeekStartsOn = 'monday' | 'sunday'
export type MonthlyRule =
  | { kind: 'day-of-month'; day: number }
  | { kind: 'ordinal-weekday'; ordinal: 1 | 2 | 3 | 4 | -1; weekday: Weekday }

export interface ReminderRecurrence {
  frequency: ReminderFrequency
  interval: number
  weekdays?: Weekday[]
  monthlyRule?: MonthlyRule
}

export interface Reminder {
  schemaVersion: typeof REMINDER_SCHEMA_VERSION
  title: string | null
  /** The current due occurrence as an absolute instant. */
  nextAt: number
  /** The nominal series occurrence represented by `nextAt`; unchanged by a snooze. */
  scheduledAt: number
  /** IANA zone captured when the reminder was created. */
  timeZone: string
  recurrence: ReminderRecurrence | null
  lastAcknowledgedAt: number | null
  createdAt: number
  updatedAt: number
}

export const DEFAULT_REMINDER_QUICK_OPTIONS = {
  firstMinutes: 60,
  secondMinutes: 180,
  tomorrowHour: 8,
} as const

export interface ReminderQuickOptions {
  firstMinutes: number
  secondMinutes: number
  tomorrowHour: number
}

export function normalizeReminderQuickOptions(value: unknown): ReminderQuickOptions {
  const options = value as Partial<ReminderQuickOptions> | null
  const number = (candidate: unknown, fallback: number, min: number, max: number) =>
    typeof candidate === 'number' && Number.isFinite(candidate)
      ? Math.min(max, Math.max(min, Math.floor(candidate)))
      : fallback
  return {
    firstMinutes: number(options?.firstMinutes, DEFAULT_REMINDER_QUICK_OPTIONS.firstMinutes, 5, 720),
    secondMinutes: number(options?.secondMinutes, DEFAULT_REMINDER_QUICK_OPTIONS.secondMinutes, 5, 1440),
    tomorrowHour: number(options?.tomorrowHour, DEFAULT_REMINDER_QUICK_OPTIONS.tomorrowHour, 0, 23),
  }
}

function currentTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

function clampInterval(value: number): number {
  return Math.min(365, Math.max(1, Math.floor(value)))
}

function validDate(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

export function normalizeReminder(value: unknown): Reminder | null {
  if (!value || typeof value !== 'object') return null
  const reminder = value as Partial<Reminder>
  if (
    reminder.schemaVersion !== REMINDER_SCHEMA_VERSION
    || !validDate(reminder.nextAt)
    || !validDate(reminder.createdAt)
    || !validDate(reminder.updatedAt)
    || typeof reminder.timeZone !== 'string'
    || (reminder.title !== null && typeof reminder.title !== 'string')
    || (reminder.lastAcknowledgedAt !== null && !validDate(reminder.lastAcknowledgedAt))
  ) return null
  const recurrence = normalizeRecurrence(reminder.recurrence)
  if (reminder.recurrence !== null && !recurrence) return null
  return {
    schemaVersion: REMINDER_SCHEMA_VERSION,
    title: reminder.title?.replace(/\s+/g, ' ').trim().slice(0, 120) || null,
    nextAt: reminder.nextAt,
    scheduledAt: validDate(reminder.scheduledAt) ? reminder.scheduledAt : reminder.nextAt,
    timeZone: reminder.timeZone || 'UTC',
    recurrence,
    lastAcknowledgedAt: reminder.lastAcknowledgedAt ?? null,
    createdAt: reminder.createdAt,
    updatedAt: reminder.updatedAt,
  }
}

export function normalizeRecurrence(value: unknown): ReminderRecurrence | null {
  if (!value || typeof value !== 'object') return null
  const rule = value as Partial<ReminderRecurrence>
  if (!['daily', 'weekly', 'monthly'].includes(String(rule.frequency))) return null
  const frequency = rule.frequency as ReminderFrequency
  const interval = clampInterval(Number(rule.interval))
  if (!Number.isFinite(Number(rule.interval))) return null
  if (frequency === 'weekly') {
    const weekdays = [...new Set((rule.weekdays ?? []).filter((day): day is Weekday =>
      Number.isInteger(day) && day >= 0 && day <= 6,
    ))].sort()
    if (weekdays.length === 0) return null
    return { frequency, interval, weekdays }
  }
  if (frequency === 'monthly') {
    const monthly = rule.monthlyRule
    if (!monthly || typeof monthly !== 'object') return null
    if (monthly.kind === 'day-of-month' && Number.isInteger(monthly.day) && monthly.day >= 1 && monthly.day <= 31) {
      return { frequency, interval, monthlyRule: { kind: 'day-of-month', day: monthly.day } }
    }
    if (
      monthly.kind === 'ordinal-weekday'
      && [1, 2, 3, 4, -1].includes(monthly.ordinal)
      && Number.isInteger(monthly.weekday)
      && monthly.weekday >= 0
      && monthly.weekday <= 6
    ) return { frequency, interval, monthlyRule: { kind: 'ordinal-weekday', ordinal: monthly.ordinal, weekday: monthly.weekday } }
    return null
  }
  return { frequency, interval }
}

export function createReminder(
  nextAt: number,
  options: Partial<Pick<Reminder, 'title' | 'timeZone' | 'recurrence'>> = {},
  now = Date.now(),
): Reminder {
  if (!validDate(nextAt)) throw new Error('A reminder must have a future firing time.')
  const recurrence = options.recurrence == null ? null : normalizeRecurrence(options.recurrence)
  if (options.recurrence != null && !recurrence) throw new Error('Invalid reminder recurrence.')
  return {
    schemaVersion: REMINDER_SCHEMA_VERSION,
    title: options.title?.replace(/\s+/g, ' ').trim().slice(0, 120) || null,
    nextAt,
    scheduledAt: nextAt,
    timeZone: options.timeZone || currentTimeZone(),
    recurrence,
    lastAcknowledgedAt: null,
    createdAt: now,
    updatedAt: now,
  }
}

export function tomorrowAt(hour: number, now = new Date()): number {
  const due = new Date(now)
  due.setDate(due.getDate() + 1)
  due.setHours(Math.min(23, Math.max(0, Math.floor(hour))), 0, 0, 0)
  return due.getTime()
}

interface ZonedDateTime {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

function zonedParts(at: number, timeZone: string): ZonedDateTime {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    hourCycle: 'h23',
  })
  const parts = Object.fromEntries(formatter.formatToParts(new Date(at)).map(({ type, value }) => [type, value]))
  return {
    year: Number(parts.year), month: Number(parts.month), day: Number(parts.day),
    hour: Number(parts.hour), minute: Number(parts.minute), second: Number(parts.second),
  }
}

function wallTime(parts: ZonedDateTime): number {
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second)
}

function compareWallTime(left: ZonedDateTime, right: ZonedDateTime): number {
  return wallTime(left) - wallTime(right)
}

/** Resolves an IANA local date/time deterministically. DST gaps move forward to the first viable local time. */
function zonedDateTimeToEpoch(target: ZonedDateTime, timeZone: string): number {
  const targetWall = wallTime(target)
  const offsets = new Set<number>()
  for (let offsetHours = -36; offsetHours <= 36; offsetHours += 6) {
    const sample = targetWall + offsetHours * 60 * 60 * 1_000
    offsets.add(wallTime(zonedParts(sample, timeZone)) - sample)
  }
  const candidates = [...offsets].map((offset) => targetWall - offset)
  const exact = candidates.filter((candidate) => compareWallTime(zonedParts(candidate, timeZone), target) === 0)
  if (exact.length > 0) return Math.min(...exact)
  const after = candidates
    .map((candidate) => ({ candidate, parts: zonedParts(candidate, timeZone) }))
    .filter(({ parts }) => compareWallTime(parts, target) > 0)
    .sort((left, right) => compareWallTime(left.parts, right.parts) || left.candidate - right.candidate)
  return after[0]?.candidate ?? Math.max(...candidates)
}

function calendarDate(parts: ZonedDateTime, addDays = 0, addMonths = 0): ZonedDateTime {
  const date = new Date(Date.UTC(parts.year, parts.month - 1 + addMonths, parts.day + addDays))
  return { ...parts, year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() }
}

function weekday(parts: ZonedDateTime): Weekday {
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay() as Weekday
}

function nextWeekly(due: ZonedDateTime, rule: ReminderRecurrence): ZonedDateTime {
  const weekdays = rule.weekdays ?? []
  const dueWeekStart = calendarDate(due, -weekday(due))
  for (let days = 1; days <= rule.interval * 7 + 7; days += 1) {
    const candidate = calendarDate(due, days)
    const candidateWeekStart = calendarDate(candidate, -weekday(candidate))
    const weeksSinceDue = Math.round((wallTime(candidateWeekStart) - wallTime(dueWeekStart)) / (7 * 24 * 60 * 60 * 1_000))
    if (weeksSinceDue % rule.interval === 0 && weekdays.includes(weekday(candidate))) return candidate
  }
  return calendarDate(due, rule.interval * 7)
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

function monthlyDate(year: number, month: number, rule: MonthlyRule, source: ZonedDateTime): ZonedDateTime {
  const candidate = { ...source }
  if (rule.kind === 'day-of-month') {
    candidate.year = year
    candidate.month = month + 1
    candidate.day = Math.min(rule.day, daysInMonth(year, month + 1))
    return candidate
  }
  const lastDay = daysInMonth(year, month + 1)
  if (rule.ordinal === -1) {
    candidate.year = year
    candidate.month = month + 1
    candidate.day = lastDay
    while (weekday(candidate) !== rule.weekday) candidate.day -= 1
    return candidate
  }
  candidate.year = year
  candidate.month = month + 1
  candidate.day = 1
  const offset = (rule.weekday - weekday(candidate) + 7) % 7
  candidate.day = 1 + offset + (rule.ordinal - 1) * 7
  if (candidate.day > lastDay) candidate.day -= 7
  return candidate
}

export function nextReminderOccurrence(reminder: Reminder): number | null {
  const rule = reminder.recurrence
  if (!rule) return null
  const due = zonedParts(reminder.scheduledAt, reminder.timeZone)
  if (rule.frequency === 'daily') {
    return zonedDateTimeToEpoch(calendarDate(due, rule.interval), reminder.timeZone)
  }
  if (rule.frequency === 'weekly') return zonedDateTimeToEpoch(nextWeekly(due, rule), reminder.timeZone)
  const monthlyRule = rule.monthlyRule
  if (!monthlyRule) return null
  return zonedDateTimeToEpoch(monthlyDate(due.year, due.month - 1 + rule.interval, monthlyRule, due), reminder.timeZone)
}

export function acknowledgeReminder(reminder: Reminder, now = Date.now()): Reminder | null {
  const nextAt = nextReminderOccurrence(reminder)
  if (nextAt === null) return null
  return { ...reminder, nextAt, scheduledAt: nextAt, lastAcknowledgedAt: now, updatedAt: now }
}

export function snoozeReminder(reminder: Reminder, durationMs: number, now = Date.now()): Reminder {
  return {
    ...reminder,
    nextAt: now + Math.max(60_000, durationMs),
    updatedAt: now,
  }
}

function calendarDayNumber(parts: ZonedDateTime): number {
  return Math.floor(Date.UTC(parts.year, parts.month - 1, parts.day) / (24 * 60 * 60 * 1_000))
}

/**
 * Returns the occurrence a recurring reminder should currently present without
 * mutating persisted acknowledgement state. This keeps stale synced records useful
 * until the occurrence update reaches every client.
 */
export function reminderDisplayAt(reminder: Reminder, now = Date.now()): number {
  if (!reminder.recurrence || reminder.nextAt >= now) return reminder.nextAt

  const rule = reminder.recurrence
  const due = zonedParts(reminder.scheduledAt, reminder.timeZone)
  const current = zonedParts(now, reminder.timeZone)

  if (rule.frequency === 'daily') {
    const daysSinceDue = Math.max(0, calendarDayNumber(current) - calendarDayNumber(due))
    const elapsedIntervals = Math.floor(daysSinceDue / rule.interval)
    let candidate = zonedDateTimeToEpoch(calendarDate(due, elapsedIntervals * rule.interval), reminder.timeZone)
    if (candidate < now) {
      candidate = zonedDateTimeToEpoch(calendarDate(due, (elapsedIntervals + 1) * rule.interval), reminder.timeZone)
    }
    return candidate
  }

  if (rule.frequency === 'weekly') {
    const dueWeekStart = calendarDate(due, -weekday(due))
    const currentDate = { ...current, hour: due.hour, minute: due.minute, second: due.second }
    for (let days = 0; days <= rule.interval * 7 + 7; days += 1) {
      const candidateParts = calendarDate(currentDate, days)
      const candidateWeekStart = calendarDate(candidateParts, -weekday(candidateParts))
      const weeksSinceDue = Math.round(
        (calendarDayNumber(candidateWeekStart) - calendarDayNumber(dueWeekStart)) / 7,
      )
      if (
        weeksSinceDue >= 0
        && weeksSinceDue % rule.interval === 0
        && (rule.weekdays ?? []).includes(weekday(candidateParts))
      ) {
        const candidate = zonedDateTimeToEpoch(candidateParts, reminder.timeZone)
        if (candidate >= now) return candidate
      }
    }
    return reminder.nextAt
  }

  if (!rule.monthlyRule) return reminder.nextAt
  const dueMonth = due.year * 12 + due.month - 1
  const currentMonth = current.year * 12 + current.month - 1
  const elapsedIntervals = Math.max(0, Math.floor((currentMonth - dueMonth) / rule.interval))
  let targetMonth = dueMonth + elapsedIntervals * rule.interval
  let candidate = zonedDateTimeToEpoch(
    monthlyDate(Math.floor(targetMonth / 12), targetMonth % 12, rule.monthlyRule, due),
    reminder.timeZone,
  )
  if (candidate < now) {
    targetMonth += rule.interval
    candidate = zonedDateTimeToEpoch(
      monthlyDate(Math.floor(targetMonth / 12), targetMonth % 12, rule.monthlyRule, due),
      reminder.timeZone,
    )
  }
  return candidate
}

export function reminderNaturalTime(
  reminder: Reminder,
  now = Date.now(),
  options: { hourCycle?: 'h23' } = {},
): string {
  const due = new Date(reminderDisplayAt(reminder, now))
  const today = new Date(now)
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: options.hourCycle ? '2-digit' : 'numeric', minute: '2-digit', ...options,
  }
  const time = new Intl.DateTimeFormat(undefined, timeOptions).format(due)
  if (due.toDateString() === today.toDateString()) return `Today ${time}`
  if (due.toDateString() === tomorrow.toDateString()) return `Tomorrow ${time}`
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', ...timeOptions }).format(due)
}

export function isReminderElapsed(reminder: Reminder, now = Date.now()): boolean {
  return !reminder.recurrence && reminder.nextAt < now
}
